import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Card,
  CardContent,
  Avatar,
  Chip,
  CircularProgress,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider
} from '@mui/material'
import { ArrowBack, Send, Refresh, Agriculture, Message as MessageIcon } from '@mui/icons-material'
import { API_BASE_URL } from '../config/api'

interface ProductionMessage {
  id: string
  conversation_id: string
  sender_id: string
  sender_type: 'admin' | 'farmer' | 'advisor'
  message: string
  message_type: string
  intervention_type?: string
  is_read: boolean
  created_at: string
  first_name: string
  last_name: string
  sender_role: string
}

interface Production {
  id: string
  production_name: string
  farmer_name: string
  farmer_id: string
  crop_variety?: string
  animal_breed?: string
  status: string
  progress_percentage: number
  current_stage: string
  start_date: string
  end_date: string
}

const AdminProductionMessages: React.FC = () => {
  const { productionId } = useParams<{ productionId: string }>()
  const navigate = useNavigate()
  const [messages, setMessages] = useState<ProductionMessage[]>([])
  const [production, setProduction] = useState<Production | null>(null)
  const [newMessage, setNewMessage] = useState('')
  const [interventionType, setInterventionType] = useState('advice')
  const [loading, setLoading] = useState(true)
  const [sendingMessage, setSendingMessage] = useState(false)
  const [conversationId, setConversationId] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [lastMessageCount, setLastMessageCount] = useState(0)
  const [notifications, setNotifications] = useState<Array<{
    id: string
    message: string
    type: string
    timestamp: Date
  }>>([])

  // const currentUser = JSON.parse(localStorage.getItem('user') || '{}')

  // Color coding for message types
  const getMessageTypeColor = (type?: string) => {
    switch (type) {
      case 'advice': return { bg: '#e8f5e8', border: '#4caf50', text: '#2e7d32' } // Green
      case 'warning': return { bg: '#fff3e0', border: '#ff9800', text: '#ef6c00' } // Orange/Yellow
      case 'support': return { bg: '#ffebee', border: '#f44336', text: '#c62828' } // Red
      case 'resource_allocation': return { bg: '#e3f2fd', border: '#2196f3', text: '#1565c0' } // Blue
      default: return { bg: '#f5f5f5', border: '#9e9e9e', text: '#424242' } // Gray
    }
  }

  const getMessageTypeLabel = (type?: string) => {
    switch (type) {
      case 'advice': return 'Advisory'
      case 'warning': return 'Warning'
      case 'support': return 'Technical Support'
      case 'resource_allocation': return 'Resource Allocation'
      case 'question': return 'Question'
      case 'update': return 'Update'
      default: return 'General'
    }
  }

  // Notification functions
  const addNotification = (message: string, type: string) => {
    const notification = {
      id: Date.now().toString(),
      message,
      type,
      timestamp: new Date()
    }
    setNotifications(prev => [...prev, notification])
    
    // Auto-remove notification after 5 seconds
    setTimeout(() => {
      setNotifications(prev => prev.filter(n => n.id !== notification.id))
    }, 5000)
  }

  const getNotificationColor = (type: string) => {
    switch (type) {
      case 'advice': return '#4caf50'
      case 'warning': return '#ff9800'
      case 'support': return '#f44336'
      case 'resource_allocation': return '#2196f3'
      case 'question': return '#9c27b0'
      case 'update': return '#ffc107'
      default: return '#9e9e9e'
    }
  }

  // Fetch production details
  const fetchProduction = async () => {
    try {
      console.log('[AdminProductionMessages] Fetching production:', productionId)
      const token = localStorage.getItem('token')
      const response = await fetch(`${API_BASE_URL}/farmer-productions/${productionId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      console.log('[AdminProductionMessages] Production response status:', response.status)
      if (response.ok) {
        const result = await response.json()
        setProduction(result.data)
        console.log('[AdminProductionMessages] Production loaded:', result.data)
        return result.data
      } else {
        const errorText = await response.text()
        console.error('[AdminProductionMessages] Failed to fetch production:', response.status, errorText)
        
        // If production fetch fails, create a mock production to allow messaging
        const mockProduction: Production = {
          id: productionId || '',
          production_name: `Production ${productionId}`,
          farmer_name: 'Unknown Farmer',
          farmer_id: '624c3828-f775-4f2a-9e2e-28988a6a2f73', // Use the farmer ID from conversation
          status: 'active',
          progress_percentage: 0,
          current_stage: 'Unknown',
          start_date: new Date().toISOString(),
          end_date: new Date().toISOString()
        }
        setProduction(mockProduction)
        console.log('[AdminProductionMessages] Using mock production data')
        return mockProduction
      }
    } catch (error) {
      console.error('[AdminProductionMessages] Error fetching production:', error)
      
      // Create mock production on error
      const mockProduction: Production = {
        id: productionId || '',
        production_name: `Production ${productionId}`,
        farmer_name: 'Unknown Farmer',
        farmer_id: '624c3828-f775-4f2a-9e2e-28988a6a2f73',
        status: 'active',
        progress_percentage: 0,
        current_stage: 'Unknown',
        start_date: new Date().toISOString(),
        end_date: new Date().toISOString()
      }
      setProduction(mockProduction)
      return mockProduction
    }
  }

  // Get or create conversation for this production
  const getOrCreateConversation = async (productionData: any) => {
    try {
      console.log('[AdminProductionMessages] Creating conversation for production:', productionId)
      const token = localStorage.getItem('token')
      const response = await fetch(`${API_BASE_URL}/admin-messaging/conversations`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          farmerId: productionData?.farmer_id || '624c3828-f775-4f2a-9e2e-28988a6a2f73',
          productionId: productionId,
          subject: `Admin Support - ${productionData?.production_name || 'Production'}`
        })
      })

      console.log('[AdminProductionMessages] Conversation response status:', response.status)
      if (response.ok) {
        const result = await response.json()
        setConversationId(result.data.conversation.id)
        console.log('[AdminProductionMessages] Conversation ready:', result.data.conversation.id)
        return result.data.conversation.id
      } else {
        const errorText = await response.text()
        console.error('[AdminProductionMessages] Failed to create conversation:', response.status, errorText)
        return null
      }
    } catch (error) {
      console.error('[AdminProductionMessages] Error creating conversation:', error)
      return null
    }
  }

  // Fetch messages for this production
  const fetchMessages = async (convId?: string) => {
    try {
      const token = localStorage.getItem('token')
      const targetConversationId = convId || conversationId
      
      if (!targetConversationId) {
        console.log('[AdminProductionMessages] No conversation ID available')
        return
      }

      const response = await fetch(`${API_BASE_URL}/admin-messaging/conversations/${targetConversationId}/messages`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      if (response.ok) {
        const result = await response.json()
        const newMessages = result.data || []
        
        // Check for new messages and create notifications
        if (lastMessageCount > 0 && newMessages.length > lastMessageCount) {
          const latestMessages = newMessages.slice(lastMessageCount)
          latestMessages.forEach((msg: ProductionMessage) => {
            // Only notify for messages from farmers (not own messages)
            if (msg.sender_type === 'farmer') {
              const senderName = `${msg.first_name} ${msg.last_name}`
              addNotification(
                `New ${getMessageTypeLabel(msg.intervention_type)} from ${senderName}`,
                msg.intervention_type || 'general'
              )
            }
          })
        }
        
        setMessages(newMessages)
        setLastMessageCount(newMessages.length)
        console.log('[AdminProductionMessages] Messages loaded:', newMessages.length)
      } else {
        console.error('[AdminProductionMessages] Failed to fetch messages')
      }
    } catch (error) {
      console.error('[AdminProductionMessages] Error fetching messages:', error)
    }
  }

  // Send message
  const sendMessage = async () => {
    if (!newMessage.trim() || !conversationId) return

    try {
      setSendingMessage(true)
      const token = localStorage.getItem('token')
      const response = await fetch(`${API_BASE_URL}/admin-messaging/messages`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          conversationId: conversationId,
          message: newMessage,
          messageType: 'text',
          interventionType: interventionType // Admin messages have intervention type
        })
      })

      if (response.ok) {
        setNewMessage('')
        await fetchMessages() // Refresh messages
        console.log('[AdminProductionMessages] Message sent successfully')
      } else {
        console.error('[AdminProductionMessages] Failed to send message')
      }
    } catch (error) {
      console.error('[AdminProductionMessages] Error sending message:', error)
    } finally {
      setSendingMessage(false)
    }
  }

  // Format message time
  const formatMessageTime = (dateString: string) => {
    try {
      const date = new Date(dateString)
      const now = new Date()
      const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60)
      
      if (diffInHours < 24) {
        return date.toLocaleTimeString('en-US', { 
          hour: '2-digit', 
          minute: '2-digit',
          hour12: false 
        })
      } else if (diffInHours < 168) {
        return date.toLocaleDateString('en-US', { 
          weekday: 'short',
          hour: '2-digit',
          minute: '2-digit',
          hour12: false
        })
      } else {
        return date.toLocaleDateString('en-US', { 
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          hour12: false
        })
      }
    } catch {
      return 'Unknown'
    }
  }

  // Initialize data
  useEffect(() => {
    const initializeData = async () => {
      setLoading(true)
      setError(null)
      console.log('[AdminProductionMessages] Starting initialization for production:', productionId)
      
      // Set a timeout to prevent infinite loading
      const timeoutId = setTimeout(() => {
        setLoading(false)
        setError('Loading timeout - please try again')
        console.error('[AdminProductionMessages] Loading timeout reached')
      }, 10000) // 10 second timeout
      
      try {
        // Fetch production data
        const productionData = await fetchProduction()
        
        // Create/get conversation
        const convId = await getOrCreateConversation(productionData)
        
        // Fetch messages if conversation exists
        if (convId) {
          await fetchMessages(convId)
        } else {
          console.log('[AdminProductionMessages] No conversation ID, but continuing anyway')
        }
        
        clearTimeout(timeoutId)
      } catch (error) {
        console.error('[AdminProductionMessages] Initialization error:', error)
        setError('Failed to load production messages')
        clearTimeout(timeoutId)
      } finally {
        setLoading(false)
        console.log('[AdminProductionMessages] Initialization complete')
      }
    }
    
    if (productionId) {
      initializeData()
    }
  }, [productionId])

  // Auto-refresh messages every 5 seconds when conversation is active
  useEffect(() => {
    if (!conversationId) return

    const interval = setInterval(() => {
      console.log('[AdminProductionMessages] Auto-refreshing messages...')
      fetchMessages()
    }, 5000) // Refresh every 5 seconds

    return () => clearInterval(interval)
  }, [conversationId])

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
        <Typography variant="h6" sx={{ ml: 2 }}>Loading production messages...</Typography>
      </Box>
    )
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Box display="flex" alignItems="center" mb={3}>
          <IconButton onClick={() => navigate('/productions')} sx={{ mr: 2 }}>
            <ArrowBack />
          </IconButton>
          <Typography variant="h4">Production Messages</Typography>
        </Box>
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="h6" color="error" gutterBottom>
            {error}
          </Typography>
          <Button 
            variant="contained" 
            onClick={() => window.location.reload()}
            sx={{ mt: 2 }}
          >
            Retry
          </Button>
        </Paper>
      </Box>
    )
  }

  return (
    <Box sx={{ p: 3, position: 'relative' }}>
      {/* Notifications */}
      <Box
        sx={{
          position: 'fixed',
          top: 16,
          right: 16,
          zIndex: 9999,
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          maxWidth: 400
        }}
      >
        {notifications.map((notification) => (
          <Paper
            key={notification.id}
            elevation={6}
            sx={{
              p: 2,
              backgroundColor: getNotificationColor(notification.type),
              color: 'white',
              borderRadius: 2,
              animation: 'slideIn 0.3s ease-out',
              '@keyframes slideIn': {
                from: {
                  transform: 'translateX(100%)',
                  opacity: 0
                },
                to: {
                  transform: 'translateX(0)',
                  opacity: 1
                }
              }
            }}
          >
            <Box display="flex" alignItems="flex-start" justifyContent="space-between">
              <Box flexGrow={1}>
                <Typography variant="body2" fontWeight="bold">
                  {notification.message}
                </Typography>
                <Typography variant="caption" sx={{ opacity: 0.8 }}>
                  {notification.timestamp.toLocaleTimeString()}
                </Typography>
              </Box>
              <IconButton
                size="small"
                onClick={() => setNotifications(prev => prev.filter(n => n.id !== notification.id))}
                sx={{ color: 'white', ml: 1 }}
              >
                ×
              </IconButton>
            </Box>
          </Paper>
        ))}
      </Box>
      {/* Header */}
      <Box display="flex" alignItems="center" mb={3}>
        <IconButton onClick={() => navigate('/productions')} sx={{ mr: 2 }}>
          <ArrowBack />
        </IconButton>
        <Box flexGrow={1}>
          <Typography variant="h4" gutterBottom>
            Production Messages
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            {production?.production_name || 'Production'} - Communication with farmer
          </Typography>
        </Box>
        <Button
          startIcon={<Refresh />}
          onClick={() => fetchMessages()}
          variant="outlined"
        >
          Refresh
        </Button>
      </Box>

      {/* Production Info */}
      {production && (
        <Card sx={{ mb: 3, bgcolor: '#f8f9fa' }}>
          <CardContent>
            <Box display="flex" alignItems="center" gap={2}>
              <Agriculture color="primary" />
              <Box flexGrow={1}>
                <Typography variant="h6">{production.production_name}</Typography>
                <Typography variant="body2" color="text.secondary">
                  Farmer: {production.farmer_name} • 
                  {production.crop_variety || production.animal_breed} • 
                  Status: {production.status} • 
                  Progress: {production.progress_percentage}% • 
                  Stage: {production.current_stage}
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      )}

      <Box display="flex" gap={3} height="calc(100vh - 300px)">
        {/* Messages Area */}
        <Paper sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
          {/* Messages List */}
          <Box sx={{ flexGrow: 1, overflow: 'auto', p: 2 }}>
            {messages.length === 0 ? (
              <Box textAlign="center" py={8}>
                <MessageIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  No messages yet
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Start a conversation with the farmer about this production
                </Typography>
              </Box>
            ) : (
              <Box display="flex" flexDirection="column" gap={2}>
                {messages.map((message) => {
                  const isAdmin = message.sender_type === 'admin'
                  const colors = getMessageTypeColor(message.intervention_type)
                  
                  return (
                    <Box
                      key={message.id}
                      display="flex"
                      justifyContent={isAdmin ? 'flex-end' : 'flex-start'}
                    >
                      <Card
                        sx={{
                          maxWidth: '70%',
                          bgcolor: isAdmin ? colors.bg : '#e3f2fd',
                          border: `1px solid ${isAdmin ? colors.border : '#2196f3'}`,
                        }}
                      >
                        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                          <Box display="flex" alignItems="center" gap={1} mb={1}>
                            <Avatar sx={{ 
                              width: 24, 
                              height: 24, 
                              fontSize: '0.75rem',
                              bgcolor: isAdmin ? '#f44336' : 
                                      message.sender_type === 'advisor' ? '#2196f3' : 
                                      '#9c27b0'
                            }}>
                              {message.first_name.charAt(0).toUpperCase()}
                            </Avatar>
                            <Typography variant="caption" fontWeight="bold">
                              {isAdmin ? 'You' : `${message.first_name} ${message.last_name}`}
                            </Typography>
                            <Chip
                              label={
                                isAdmin ? 'Administrator' :
                                message.sender_type === 'advisor' ? 'Advisor' :
                                'Farmer'
                              }
                              size="small"
                              sx={{
                                height: 16,
                                fontSize: '0.6rem',
                                bgcolor: isAdmin ? '#ffebee' : 
                                        message.sender_type === 'advisor' ? '#e3f2fd' : 
                                        '#f3e5f5',
                                color: isAdmin ? '#c62828' : 
                                       message.sender_type === 'advisor' ? '#1565c0' : 
                                       '#7b1fa2'
                              }}
                            />
                            {message.intervention_type && (
                              <Chip
                                label={getMessageTypeLabel(message.intervention_type)}
                                size="small"
                                sx={{
                                  bgcolor: colors.bg,
                                  color: colors.text,
                                  border: `1px solid ${colors.border}`,
                                  fontSize: '0.7rem',
                                  height: 20
                                }}
                              />
                            )}
                            <Typography variant="caption" color="text.secondary">
                              {formatMessageTime(message.created_at)}
                            </Typography>
                          </Box>
                          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                            {message.message}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Box>
                  )
                })}
              </Box>
            )}
          </Box>

          <Divider />

          {/* Message Input */}
          <Box sx={{ p: 2 }}>
            <Box display="flex" gap={2} mb={2}>
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel>Message Type</InputLabel>
                <Select
                  value={interventionType}
                  onChange={(e) => setInterventionType(e.target.value)}
                  label="Message Type"
                >
                  <MenuItem value="advice">
                    <Box display="flex" alignItems="center" gap={1}>
                      <Box
                        sx={{
                          width: 12,
                          height: 12,
                          borderRadius: '50%',
                          bgcolor: '#4caf50'
                        }}
                      />
                      Advisory
                    </Box>
                  </MenuItem>
                  <MenuItem value="warning">
                    <Box display="flex" alignItems="center" gap={1}>
                      <Box
                        sx={{
                          width: 12,
                          height: 12,
                          borderRadius: '50%',
                          bgcolor: '#ff9800'
                        }}
                      />
                      Warning
                    </Box>
                  </MenuItem>
                  <MenuItem value="support">
                    <Box display="flex" alignItems="center" gap={1}>
                      <Box
                        sx={{
                          width: 12,
                          height: 12,
                          borderRadius: '50%',
                          bgcolor: '#f44336'
                        }}
                      />
                      Technical Support
                    </Box>
                  </MenuItem>
                  <MenuItem value="resource_allocation">
                    <Box display="flex" alignItems="center" gap={1}>
                      <Box
                        sx={{
                          width: 12,
                          height: 12,
                          borderRadius: '50%',
                          bgcolor: '#2196f3'
                        }}
                      />
                      Resource Allocation
                    </Box>
                  </MenuItem>
                </Select>
              </FormControl>
            </Box>

            <Box display="flex" gap={2}>
              <TextField
                fullWidth
                multiline
                rows={3}
                placeholder="Type your message to the farmer..."
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                disabled={sendingMessage}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault()
                    sendMessage()
                  }
                }}
              />
              <Button
                variant="contained"
                startIcon={sendingMessage ? <CircularProgress size={16} /> : <Send />}
                onClick={sendMessage}
                disabled={!newMessage.trim() || sendingMessage}
                sx={{ minWidth: 120 }}
              >
                {sendingMessage ? 'Sending...' : 'Send'}
              </Button>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              Press Enter to send, Shift+Enter for new line. Messages will be visible to the farmer.
            </Typography>
          </Box>
        </Paper>

        {/* Message Type Legend */}
        {/* <Paper sx={{ width: 250, p: 2 }}>
          <Typography variant="h6" gutterBottom>
            Message Types
          </Typography>
          <Box display="flex" flexDirection="column" gap={1}>
            {[
              { type: 'advice', label: 'Advisory', description: 'General advice and guidance' },
              { type: 'warning', label: 'Warning', description: 'Important alerts and warnings' },
              { type: 'support', label: 'Technical Support', description: 'Technical assistance needed' },
              { type: 'resource_allocation', label: 'Resource Allocation', description: 'Resource planning and allocation' }
            ].map((item) => {
              const colors = getMessageTypeColor(item.type)
              return (
                <Card
                  key={item.type}
                  sx={{
                    bgcolor: colors.bg,
                    border: `1px solid ${colors.border}`,
                    cursor: 'pointer',
                    '&:hover': { opacity: 0.8 }
                  }}
                  onClick={() => setInterventionType(item.type)}
                >
                  <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                    <Typography variant="subtitle2" sx={{ color: colors.text, fontWeight: 'bold' }}>
                      {item.label}
                    </Typography>
                    <Typography variant="caption" sx={{ color: colors.text }}>
                      {item.description}
                    </Typography>
                  </CardContent>
                </Card>
              )
            })}
          </Box>
        </Paper> */}
      </Box>
    </Box>
  )
}

export default AdminProductionMessages
